<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';
import CsmlVisualBuilder from '../components/visualBuilder/CsmlVisualBuilder.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const uiFlags = useMapGetter('agentBots/getUIFlags');

const builderRef = ref(null);
const isLoading = ref(true);
const bot = ref({});
const initialCsml = ref('');

const accountId = computed(() => store.getters.getCurrentAccountId);
const botId = computed(() => Number(route.params.botId));
const isSaving = computed(() => uiFlags.value.isUpdating);

const goBack = () => {
  router.push(frontendURL(`accounts/${accountId.value}/settings/agent-bots`));
};

const loadBot = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('agentBots/show', botId.value);
    const loaded = store.getters['agentBots/getBot'](botId.value);
    if (!loaded || loaded.bot_type !== 'csml') {
      useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.INVALID_BOT_TYPE'));
      goBack();
      return;
    }
    bot.value = loaded;
    initialCsml.value = loaded?.bot_config?.csml_content || '';
    isLoading.value = false;
  } catch (_) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.SAVE_ERROR'));
    goBack();
  }
};

const saveBot = async ({ removeAvatar, ...botData }) => {
  try {
    await store.dispatch('agentBots/update', {
      id: botId.value,
      data: {
        ...botData,
        outgoing_url: '',
      },
    });
    // Purge the old avatar only once the update has been accepted.
    if (removeAvatar && !botData.avatar) {
      await store.dispatch('agentBots/deleteAgentBotAvatar', botId.value);
    }
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.SAVE_SUCCESS'));
    builderRef.value?.markSaved();
    goBack();
  } catch (_) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.SAVE_ERROR'));
  }
};

onMounted(loadBot);
</script>

<template>
  <div class="h-full w-full">
    <div
      v-if="isLoading"
      class="flex items-center justify-center h-full text-sm text-n-slate-11"
    >
      {{ t('AGENT_BOTS.LOADING_EDITOR') }}
    </div>
    <CsmlVisualBuilder
      v-else
      ref="builderRef"
      mode="edit"
      :initial-bot="bot"
      :initial-csml="initialCsml"
      :is-saving="isSaving"
      @save="saveBot"
      @cancel="goBack"
    />
  </div>
</template>
